import { useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, Plus, Search, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, EmptyState, PageHeader, TableSkeleton, cx } from "../components/ui";
import { BookingPanel } from "../components/BookingPanel";
import { timeOnly } from "../lib/datetime";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import { formatDate, formatMoney } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import type { Booking, BookingStatus } from "../api/types";

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
  confirmed: "bg-sky-50 text-sky-700 ring-sky-600/20",
  checked_in: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  in_progress: "bg-violet-50 text-violet-700 ring-violet-600/20",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  no_show: "bg-rose-50 text-rose-700 ring-rose-600/20",
  cancelled: "bg-slate-100 text-slate-500 ring-slate-400/20",
};

//: The moves a front desk actually reaches for, in order. The server owns the
//: transition table; this is the subset of statuses this screen offers.
const NEXT: Record<string, string> = {
  pending: "confirmed",
  confirmed: "checked_in",
  checked_in: "in_progress",
  in_progress: "completed",
};

/**
 * The bookings register: everything coming up, filterable, actionable.
 *
 * One row per appointment, newest first in the window. The day view (Calendar)
 * refuses to hide failures; this register *does* filter them out by default,
 * because the two screens answer different questions -- who is coming in vs
 * what happened. A cancelled booking appears here only when asked.
 *
 * The row actions mirror FR-10.4 and FR-10.5: walk a booking forward along its
 * lifecycle, or cancel it with the reason the metrics are built from.
 */
export function BookingsPage() {
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();
  const queryClient = useQueryClient();
  // The route already keeps employees off this screen; this second gate keeps
  // the create affordance honest if the component is ever embedded elsewhere.
  const canCreate = isManagerOrAbove(user?.role);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [showCancelled, setShowCancelled] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["bookings-list", status, employeeFilter, showCancelled],
    queryFn: ({ signal }) =>
      api.bookings(
        {
          status: status || undefined,
          employee: employeeFilter || undefined,
          include_cancelled: showCancelled ? "true" : undefined,
          page_size: 100,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    queryFn: ({ signal }) => api.employees(signal),
    staleTime: 10 * 60_000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) => api.setBookingStatus(id, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["bookings"] }),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.cancelBooking(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      setCancelling(null);
    },
  });

  const all = query.data?.results ?? [];
  const rows = useMemo(() => {
    if (!search.trim()) return all;
    const needle = search.toLowerCase();
    return all.filter((row) =>
      [row.contact_name, row.contact_phone, row.service_name, row.employee_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle)),
    );
  }, [all, search]);

  const cancelError = cancelMutation.error instanceof ApiError ? cancelMutation.error : null;

  return (
    <>
      <PageHeader
        title="Bookings"
        subtitle={
          query.data
            ? `${rows.length} booking${rows.length === 1 ? "" : "s"}`
            : "Every appointment, and the rules that keep it from double-booking"
        }
        actions={
          <>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Filter by status"
              className="rounded-md border-0 bg-white px-2 py-1.5 text-xs ring-1 ring-inset ring-slate-300"
            >
              <option value="">All statuses</option>
              {(["pending", "confirmed", "checked_in", "in_progress", "completed", "no_show", "cancelled"] as BookingStatus[]).map(
                (s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ")}
                  </option>
                ),
              )}
            </select>
            <select
              value={employeeFilter}
              onChange={(event) => setEmployeeFilter(event.target.value)}
              aria-label="Filter by provider"
              className="rounded-md border-0 bg-white px-2 py-1.5 text-xs ring-1 ring-inset ring-slate-300"
            >
              <option value="">All providers</option>
              {(employees.data ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.display_name}
                </option>
              ))}
            </select>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(event) => setShowCancelled(event.target.checked)}
                className="size-3.5 rounded border-slate-400"
              />
              Show cancelled
            </label>
            {canCreate ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setBookingOpen(true)}
                className="inline-flex items-center gap-1.5"
              >
                <Plus size={14} /> New booking
              </Button>
            ) : null}
          </>
        }
      />

      {successMessage ? (
        <div
          role="status"
          className="mb-4 flex items-center justify-between rounded-md bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20 shadow-xs"
        >
          <span className="flex items-center gap-2">
            <Check size={16} className="text-emerald-600 shrink-0" />
            {successMessage}
          </span>
          <button
            type="button"
            onClick={() => setSuccessMessage(null)}
            className="text-emerald-700 hover:text-emerald-900"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        </div>
      ) : null}

      {bookingOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <BookingPanel
            onClose={() => setBookingOpen(false)}
            onCreated={() => {
              setSuccessMessage("Booking created successfully.");
              void queryClient.invalidateQueries({ queryKey: ["bookings-list"] });
              void queryClient.invalidateQueries({ queryKey: ["bookings"] });
              setBookingOpen(false);
            }}
          />
        </div>
      ) : null}

      <div className="mb-4 relative max-w-md">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search person, phone or service"
          aria-label="Search bookings"
          className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
        />
      </div>

      {query.isPending ? (
        <Card padded={false}>
          <TableSkeleton rows={6} />
        </Card>
      ) : query.isError ? (
        <Card>
          <EmptyState title="Could not load bookings" body="Please try again." />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen size={28} />}
            title="No bookings here"
            body={
              all.length === 0
                ? "Mark a lead as Won with an agreed time and the slot appears here automatically — or open the Calendar and reserve one."
                : "Try a different filter or search."
            }
            action={
              canCreate ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setBookingOpen(true)}
                  className="inline-flex items-center gap-1.5"
                >
                  <Plus size={14} /> New booking
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5">Who</th>
                <th className="px-4 py-2.5">Service</th>
                <th className="px-4 py-2.5">Provider</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className={cx("align-top", row.status === "cancelled" && "opacity-60")}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium tabular-nums text-slate-900">
                      {formatDate(row.starts_at, timeZone)}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {timeOnly(row.starts_at, timeZone)}–{timeOnly(row.ends_at, timeZone)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{row.contact_name || "Blocked slot"}</p>
                    {row.contact_phone ? (
                      <p className="mt-0.5 text-xs text-slate-500">{row.contact_phone}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-800">{row.service_name || "—"}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{row.minutes}m</p>
                    {row.price?.amount ? (
                      <p className="mt-0.5 text-xs tabular-nums text-slate-500">{formatMoney(row.price)}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{row.employee_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cx(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize",
                        STATUS_TONE[row.status] ?? STATUS_TONE.pending,
                      )}
                    >
                      {row.status_display}
                    </span>
                    {row.status === "cancelled" && row.cancellation_reason ? (
                      <p className="mt-0.5 text-[11px] italic text-slate-400">{row.cancellation_reason}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowActions
                      row={row}
                      busy={statusMutation.isPending || cancelMutation.isPending}
                      onStatus={(next) => statusMutation.mutate({ id: row.id, next })}
                      onCancel={() => setCancelling(row.id)}
                    />
                    {cancelling === row.id ? (
                      <CancelPrompt
                        row={row}
                        busy={cancelMutation.isPending}
                        error={cancelError}
                        onConfirm={(reason) => cancelMutation.mutate({ id: row.id, reason })}
                        onClose={() => setCancelling(null)}
                      />
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function RowActions({
  row,
  busy,
  onStatus,
  onCancel,
}: {
  row: Booking;
  busy: boolean;
  onStatus: (next: string) => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {NEXT[row.status] ? (
        <button
          onClick={() => onStatus(NEXT[row.status]!)}
          disabled={busy}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          Mark {NEXT[row.status]!.replace("_", " ")}
        </button>
      ) : null}
      {row.status !== "cancelled" && row.status !== "no_show" ? (
        <button
          onClick={onCancel}
          disabled={busy}
          title="Cancel this booking"
          className="rounded-md p-1.5 text-slate-400 ring-1 ring-inset ring-rose-600/20 hover:bg-rose-50 hover:text-rose-700"
        >
          <X size={13} />
        </button>
      ) : null}
    </div>
  );
}

function CancelPrompt({
  row,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  row: Booking;
  busy: boolean;
  error: ApiError | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <div className="mt-2 w-[20rem] rounded-lg bg-rose-50 p-2.5 text-left ring-1 ring-inset ring-rose-600/25">
      <p className="mb-1 text-[11px] font-semibold text-rose-900">
        Cancel {row.contact_name || "this booking"}?
      </p>
      <input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why? This reason is reported."
        aria-label="Cancellation reason"
        className="w-full rounded-md border-0 bg-white px-2 py-1 text-xs ring-1 ring-inset ring-rose-600/20"
      />
      {error ? (
        <p role="alert" className="mt-1 text-[11px] text-rose-700">
          {error.message}
        </p>
      ) : null}
      <div className="mt-1.5 flex items-center gap-1">
        <button
          onClick={() => onConfirm(reason)}
          disabled={busy || !reason.trim()}
          className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-700 disabled:opacity-50"
        >
          {busy ? "Cancelling…" : "Cancel"}
        </button>
        <button onClick={onClose} className="rounded-md px-2 py-1 text-[11px] text-rose-700">
          Keep
        </button>
      </div>
    </div>
  );
}